import React from 'react';
import { FileText, Maximize2, Minimize2 } from 'lucide-react';

const formatCurrency = (value, currency) => {
  const symbols = { USD: '$', EUR: '€', BRL: 'R$', GBP: '£' };
  const symbol = symbols[currency] || (currency ? currency + ' ' : '$');
  const num = parseFloat(value);
  if (isNaN(num)) return null;
  return `${symbol}${num.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const Field = ({ value, placeholder, bold = false, className = '' }) => {
  const isEmpty = !value || value.toString().trim() === '';
  return (
    <span className={`${isEmpty ? 'text-gray-400 italic' : 'text-gray-800'} ${bold && !isEmpty ? 'font-bold' : ''} ${className}`}>
      {isEmpty ? placeholder : value}
    </span>
  );
};

const SectionHeader = ({ children }) => (
  <h3 className="text-sm font-bold text-[#2B4B6A] uppercase tracking-wide border-b-2 border-[#2B4B6A]/40 pb-2 mb-4">
    {children}
  </h3>
);

const TableRow = ({ label, value, placeholder }) => (
  <div className="flex justify-between items-baseline py-2.5 border-b border-gray-200/70">
    <span className="text-sm text-gray-500">{label}</span>
    <span className="text-sm text-right ml-4 flex-shrink-0 font-semibold">
      <Field value={value} placeholder={placeholder} />
    </span>
  </div>
);

const LOIPreview = ({ formData, isFullScreen, onToggleFullScreen }) => {
  const buyer = formData?.buyer || {};
  const seller = formData?.seller || {};
  const product = formData?.product || {};
  const terms = formData?.terms || {};
  const timeline = formData?.timeline || {};
  const signatory = formData?.signatory || {};

  const buyerCityLine = [buyer.city, buyer.state, buyer.country]
    .filter(Boolean)
    .join(', ');
  const buyerCityFull = buyerCityLine
    ? `${buyerCityLine}${buyer.zip ? ' ' + buyer.zip : ''}`
    : null;

  const sellerCityLine = [seller.city, seller.state, seller.country]
    .filter(Boolean)
    .join(', ');
  const sellerCityFull = sellerCityLine
    ? `${sellerCityLine}${seller.zip ? ' ' + seller.zip : ''}`
    : null;

  const buyerContact = [buyer.email, buyer.phone].filter(Boolean).join(' | ') || null;
  const sellerContact = [seller.email, seller.phone].filter(Boolean).join(' | ') || null;

  const unitPriceFormatted = formatCurrency(terms.price_per_unit, terms.currency);
  const unitPriceDisplay = unitPriceFormatted
    ? `${unitPriceFormatted} / ${terms.quantity_unit || 'Metric Tons'}`
    : null;

  const totalValueFormatted = formatCurrency(terms.total_value, terms.currency);
  const totalValueDisplay = totalValueFormatted || null;

  const quantityDisplay =
    terms.quantity || terms.quantity_unit
      ? `${terms.quantity || '—'} ${terms.quantity_unit || 'Metric Tons'}`
      : null;

  const commodityDisplay = product.commodity_type === 'Other'
    ? (product.commodity_type_other || null)
    : (product.commodity_type || null);

  const originPortDisplay = terms.origin_port === 'Other'
    ? (terms.origin_port_other || null)
    : (terms.origin_port || null);

  const destPortDisplay = terms.destination_port === 'Other'
    ? (terms.destination_port_other || null)
    : (terms.destination_port || null);

  const rawLoiNumber = formData?.loi_number || '00000000';
  const loiNumber = rawLoiNumber.replace(/^LOI-/i, '');

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      {/* Navy header bar */}
      <div className="bg-[#2B4B6A] text-white px-5 py-3.5 flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider opacity-80">
          <FileText size={14} /> Live LOI Preview
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm font-bold tracking-wide" style={{ fontFamily: 'Georgia, serif' }}>
            LOI # {loiNumber}
          </span>
          {onToggleFullScreen && (
            <button
              onClick={onToggleFullScreen}
              className="flex items-center gap-1.5 text-white/70 hover:text-white text-xs transition-colors"
              title={isFullScreen ? 'Close full screen' : 'View full screen'}
            >
              {isFullScreen ? (
                <>
                  <Minimize2 className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Close Full Screen</span>
                </>
              ) : (
                <>
                  <Maximize2 className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Full Screen</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Document body */}
      <div className="px-7 py-8" style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}>
        {/* Row 1: Buyer Logo (left) | LETTER OF INTENT (center) | Trados Logo (right) */}
        <div className="flex items-start justify-between mb-10">
          {/* Buyer logo - left */}
          <div className="w-[130px] flex-shrink-0 flex justify-start overflow-hidden">
            {buyer.logo_url ? (
              <img
                src={buyer.logo_url}
                alt="Logo"
                className="object-contain object-left"
                style={{ height: `${Math.min(Math.round(80 * (buyer.logo_size || 100) / 100), 100)}px`, maxWidth: '130px' }}
              />
            ) : (
              <div className="w-[120px] h-[70px] border-2 border-dashed border-gray-200 rounded-lg flex items-center justify-center">
                <span className="text-gray-300 text-sm">Your Logo</span>
              </div>
            )}
          </div>

          {/* Title - center */}
          <div className="flex-1 text-center pt-2">
            <h1 className="text-2xl font-bold text-[#2B4B6A] tracking-[0.15em] uppercase" style={{ fontFamily: 'Georgia, serif' }}>
              Letter of Intent
            </h1>
            <div className="w-16 h-[2px] bg-[#2B4B6A] mx-auto mt-2" />
          </div>

          {/* Trados logo - right */}
          <div className="w-[80px] flex-shrink-0 flex justify-end pt-1">
            <img
              src="https://www.trados.co/wp-content/uploads/2026/03/Trados-Trade-Verified-square.png"
              alt="Trados"
              className="h-12 object-contain"
            />
          </div>
        </div>

        {/* Buyer info block + Date */}
        <div className="flex justify-between items-start mb-8">
          <div className="leading-relaxed">
            <div className="text-lg font-bold text-gray-900 mb-1">
              <Field value={buyer.company_name} placeholder="Your Company Name" bold />
            </div>
            <div className="text-base text-gray-600">
              <Field value={buyer.address} placeholder="123 Street Address, Suite 100" />
            </div>
            {buyerCityFull && (
              <div className="text-base text-gray-600">{buyerCityFull}</div>
            )}
            <div className="text-base text-gray-600">
              <Field value={buyerContact} placeholder="email@company.com | +1 (000) 000-0000" />
            </div>
            {buyer.website && (
              <div className="text-sm text-gray-500">{buyer.website}</div>
            )}
          </div>
          <div className="text-base text-gray-500 flex-shrink-0 ml-4 pt-1">
            <Field
              value={formatDate(signatory.signing_date)}
              placeholder="March 16, 2026"
            />
          </div>
        </div>

        {/* Company Description */}
        {buyer.company_description && (
          <div className="mb-8">
            <SectionHeader>Company Description</SectionHeader>
            <p className="text-base text-gray-700 leading-relaxed">{buyer.company_description}</p>
          </div>
        )}
        {!buyer.company_description && <div className="mb-6" />}

        {/* TO (SELLER) */}
        <div className="mb-8">
          <SectionHeader>To (Seller)</SectionHeader>
          <div className="leading-relaxed space-y-0.5">
            <div className="text-lg font-bold text-gray-900">
              <Field value={seller.company_name} placeholder="Seller Company Name" bold />
            </div>
            <div className="text-base text-gray-600">
              <Field value={seller.address} placeholder="123 Street Address" />
            </div>
            {sellerCityFull && (
              <div className="text-base text-gray-600">{sellerCityFull}</div>
            )}
            <div className="text-base text-gray-600">
              <Field value={sellerContact} placeholder="seller@company.com | +55 00 0000-0000" />
            </div>
          </div>
        </div>

        {/* RE line */}
        <div className="text-base font-semibold text-gray-800 mb-6">
          RE: Purchase of{' '}
          <Field value={commodityDisplay} placeholder="Commodity" /> from{' '}
          <Field value={seller.company_name} placeholder="Seller" />
        </div>

        {/* Body text */}
        <div className="text-base text-gray-700 leading-relaxed mb-8 space-y-3">
          <p>Dear Sir/Madam,</p>
          <p>
            We, <Field value={buyer.company_name} placeholder="Your Company Name" bold />,
            hereby express our intent to purchase the following commodity from{' '}
            <Field value={seller.company_name} placeholder="Seller Company Name" bold /> under
            the terms and conditions outlined below:
          </p>
        </div>

        {/* Product Description & Quality Specs */}
        <div className="mb-8">
          <SectionHeader>Product Description & Quality Specifications</SectionHeader>
          <div className="text-base text-gray-700 leading-relaxed space-y-2">
            <p><Field value={product.description} placeholder="Product description..." /></p>
            {product.specifications && (
              <p className="text-gray-600">{product.specifications}</p>
            )}
          </div>
        </div>

        {/* Two-column: Commercial Terms + Financial Details */}
        <div className="grid grid-cols-2 gap-5 mb-5">
          {/* Commercial Terms - light blue */}
          <div className="rounded-lg p-5 border border-blue-200/60" style={{ backgroundColor: '#F0F7FF' }}>
            <SectionHeader>Commercial Terms</SectionHeader>
            <TableRow label="Commodity" value={commodityDisplay} placeholder="Commodity" />
            <TableRow label="Quantity" value={quantityDisplay} placeholder="00,000 MT" />
            <TableRow label="Price Per Unit*" value={unitPriceDisplay} placeholder="$0.00 / MT" />
            <TableRow label="Total Value" value={totalValueDisplay} placeholder="$0.00" />
            <TableRow label="Incoterms" value={terms.incoterms} placeholder="FOB / CIF" />
            <TableRow label="Payment" value={terms.payment_terms} placeholder="Letter of Credit" />
            <TableRow label="Timeframe" value={terms.request_timeframe === 'Other' ? terms.request_timeframe_other : terms.request_timeframe} placeholder="Spot / 1 Year" />
          </div>

          {/* Financial Details - light mint */}
          <div className="rounded-lg p-5 border border-emerald-200/60" style={{ backgroundColor: '#F0FDF9' }}>
            <SectionHeader>Financial Details</SectionHeader>
            <TableRow label="Bank Name" value={terms.bank_name} placeholder="Bank Name" />
            <TableRow label="Top 50 Bank" value={terms.top_50_bank} placeholder="Yes / No" />
            <TableRow label="Proof of Funds" value={terms.proof_of_funds} placeholder="Yes / No" />
            {(terms.proof_of_funds === 'Yes' || !terms.proof_of_funds) && (
              <TableRow label="Proof Details" value={terms.proof_of_funds_details} placeholder="Details" />
            )}
          </div>
        </div>

        {/* Two-column: Logistics + Timeline */}
        <div className="grid grid-cols-2 gap-5 mb-8">
          {/* Logistics - light ivory */}
          <div className="rounded-lg p-5 border border-amber-200/60" style={{ backgroundColor: '#FFFDF5' }}>
            <SectionHeader>Logistics</SectionHeader>
            <TableRow label="Port of Origin" value={originPortDisplay} placeholder="Santos - Brazil" />
            <TableRow label="Destination" value={destPortDisplay} placeholder="Destination Port" />
            <TableRow label="Inspection" value={timeline.inspection_terms} placeholder="SGS / Bureau Veritas" />
          </div>

          {/* Timeline - light rose */}
          <div className="rounded-lg p-5 border border-rose-200/60" style={{ backgroundColor: '#FFF5F3' }}>
            <SectionHeader>Timeline</SectionHeader>
            <TableRow label="LOI Valid Until" value={formatDate(timeline.validity_date)} placeholder="Valid Until Date" />
            <TableRow label="Duration" value={timeline.contract_duration} placeholder="12 Months" />
            <TableRow label="Shipping" value={timeline.shipping_schedule} placeholder="To be determined" />
          </div>
        </div>

        {/* Special Conditions */}
        {timeline.special_conditions && timeline.special_conditions.trim() !== '' && (
          <div className="mb-8">
            <SectionHeader>Special Conditions</SectionHeader>
            <p className="text-base text-gray-700 leading-relaxed whitespace-pre-line">
              {timeline.special_conditions}
            </p>
          </div>
        )}

        {/* Extra LOI Details */}
        {formData?.extra_details && formData.extra_details.trim() !== '' && (
          <div className="mb-8">
            <SectionHeader>Additional Details & Requirements</SectionHeader>
            <p className="text-base text-gray-700 leading-relaxed whitespace-pre-wrap">
              {formData.extra_details}
            </p>
          </div>
        )}

        {/* Closing paragraph */}
        <div className="text-sm text-gray-500 leading-relaxed mb-10 mt-8 border-t-2 border-gray-100 pt-5 italic">
          This Letter of Intent is non-binding and serves solely to express the genuine interest
          of the undersigned party in negotiating and concluding a formal purchase agreement for
          the commodity described above, subject to mutually agreed terms and conditions.
        </div>

        {/* Signature area with stamp */}
        <div className="mb-10">
          <p className="text-sm text-gray-500 uppercase tracking-widest font-semibold mb-6">
            Authorized Signatory:
          </p>
          <div className="flex items-end gap-4">
            {/* Signature + info */}
            <div>
              <div className="-mb-1">
                {signatory.signature_image_url ? (
                  <img
                    src={signatory.signature_image_url}
                    alt="Signature"
                    className="object-contain"
                    style={{ height: `${Math.round(56 * (signatory.signature_size || 100) / 100)}px` }}
                  />
                ) : (
                  <div className="w-56 border-b-2 border-dashed border-gray-300 pb-10 mb-1">
                    <span className="text-gray-400 text-sm italic">Signature</span>
                  </div>
                )}
              </div>
              <div className="w-48 border-t border-gray-300 mb-1" />
              <div className="leading-relaxed">
                <div className="font-bold text-gray-900 text-lg">
                  <Field value={signatory.full_name} placeholder="Full Name" bold />
                </div>
                <div className="italic text-gray-600 text-base mt-0.5">
                  <Field
                    value={
                      signatory.title && buyer.company_name
                        ? `${signatory.title} / ${buyer.company_name}.`
                        : signatory.title || null
                    }
                    placeholder="Title / Company"
                  />
                </div>
                <div className="text-base text-gray-500 mt-1">
                  <Field
                    value={formatDate(signatory.signing_date)}
                    placeholder="Signing Date"
                  />
                </div>
              </div>
            </div>
            {/* Company Stamp */}
            {buyer.stamp_url && (
              <div className="flex-shrink-0 self-center">
                <img
                  src={buyer.stamp_url}
                  alt="Company Stamp"
                  className="object-contain"
                  style={{
                    width: `${Math.round(112 * (buyer.stamp_size || 100) / 100)}px`,
                    height: `${Math.round(112 * (buyer.stamp_size || 100) / 100)}px`,
                    transform: `rotate(${buyer.stamp_rotation || 0}deg)`,
                    mixBlendMode: 'multiply',
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t-2 border-gray-200 pt-5">
          <div className="flex items-center gap-3">
            <img
              src="https://www.trados.co/wp-content/uploads/2026/03/Trados-Trade-Verified-square.png"
              alt="Trados"
              className="h-10 object-contain"
            />
            <div className="flex flex-col">
              <span className="text-xs text-gray-500 font-medium">Created by Trados</span>
              <span className="text-[10px] text-gray-400">www.trados.app</span>
            </div>
          </div>
          <div className="w-16 h-16 bg-gray-50 border border-gray-200 rounded flex items-center justify-center">
            <span className="text-gray-400 text-[9px]">QR Code</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LOIPreview;
